/**
 * COPIED from pages/dev-agent/components/metadataEditor/MetadataEditorPanelHeader.tsx.
 * No store coupling — pure presentational header; `OpenFile` resolves to the
 * local multi-agent editor types.
 */
import type { ReactNode } from "react";
import { bareClassName } from "@/pages/multi-agent/ide/utils/bareClassName";
import type { OpenFile } from "./types";

export function MetadataEditorPanelHeader({
  isLoadingMetadataFile,
  activeFile,
  activeBadge,
  extraActions,
  onRunTest,
  activeFileId,
  editorContent,
  isBusy,
  isTestRunning,
  onClose,
}: {
  isLoadingMetadataFile: boolean;
  activeFile: OpenFile | null;
  activeBadge: string | undefined;
  extraActions?: ReactNode;
  onRunTest?: (className: string) => void;
  activeFileId: string | null;
  editorContent: string;
  isBusy?: boolean;
  /** A user/agent test run is in flight — disables Run Test and shows progress. */
  isTestRunning?: boolean;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50 flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
        {isLoadingMetadataFile && (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent shrink-0" />
        )}
        {activeFile && (
          <span
            className="text-sm font-medium truncate flex items-center gap-1.5 min-w-0"
            title={activeFile.name}
          >
            {activeBadge && (
              <span
                className={`text-[10px] font-bold px-1 rounded shrink-0 ${
                  activeBadge === "R"
                    ? "bg-green-500/20 text-green-400"
                    : activeBadge === "C"
                      ? "bg-blue-500/20 text-blue-400"
                      : "bg-yellow-500/20 text-yellow-400"
                }`}
              >
                {activeBadge}
              </span>
            )}
            <span className="truncate">{activeFile.name}</span>
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {extraActions}
        {onRunTest &&
          activeFileId &&
          editorContent.toLowerCase().includes("@istest") &&
          activeFile && (
            <button
              type="button"
              onClick={() => {
                const className = bareClassName(activeFile.name);
                onRunTest(className);
              }}
              disabled={isBusy || isTestRunning}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isTestRunning ? (
                <>
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-green-400 border-t-transparent" />
                  Running…
                </>
              ) : (
                <>▶ Run Test</>
              )}
            </button>
          )}
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 rounded hover:bg-muted/70 border border-transparent hover:border-border/30"
        >
          Close
        </button>
      </div>
    </div>
  );
}
